import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogOut, Monitor, Moon, Settings2, Sun, User } from "lucide-react";
import { useTheme, type Theme } from "../lib/theme";
import { useLogout, useMe } from "../api/auth";
import { useAdminCanonicalAliasProposals } from "../api/queries";

/**
 * Avatar-dropdown identity menu.
 *
 * Modern AI-app pattern (Slack/Linear/Notion/ChatGPT): personal
 * preferences and rarely-used actions live behind the avatar. Things
 * we relegate from the top-bar chip soup to here:
 *   - Profile settings link
 *   - Theme picker (system / light / dark)
 *   - Logout
 *
 * Why a segmented picker for theme inside the menu instead of a
 * cycling button outside: cycling forces operators to mentally model
 * the cycle order. Segmented shows all three states at once and is
 * tap-direct on mobile, no hover required.
 */

const COLOR_HEX: Record<string, string> = {
  orange: "#F39200",
  red: "#E63946",
  green: "#2A9D58",
  pink: "#E91E63",
};

export function UserMenu() {
  const me = useMe();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const isAdmin = me.data?.organisation.role === "administrateur";
  const aliasProposals = useAdminCanonicalAliasProposals(isAdmin);

  // Close on outside click + Escape. Plain DOM listeners — no popover
  // lib so we don't grow the bundle for one menu.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!me.data) return null;
  const profile = me.data.profil;
  const initial = profile.prenom[0]?.toUpperCase() ?? "?";
  const colorHex = COLOR_HEX[profile.color] ?? "#9b9b9b";
  const pendingAliasCount = aliasProposals.data?.length ?? 0;

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        title={`${profile.prenom} ${profile.nom} — menu`}
        className="flex items-center gap-1.5 min-h-9 pl-1 pr-2 rounded-full ring-1 ring-black/10 dark:ring-white/10 hover:ring-ink/40 dark:hover:ring-white/40 hover:bg-white dark:hover:bg-surface-dark transition"
      >
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt=""
            className="h-7 w-7 rounded-full object-cover"
          />
        ) : (
          <span
            className="h-7 w-7 rounded-full text-white text-xs font-semibold flex items-center justify-center"
            style={{ backgroundColor: colorHex }}
          >
            {initial}
          </span>
        )}
        <span className="hidden md:inline text-sm text-ink dark:text-ink-dark">
          {profile.prenom}
        </span>
        {isAdmin && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-chip-orange/10 text-chip-orange font-medium leading-none">
            Admin
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 text-muted dark:text-muted-dark transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-xl bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 shadow-lg overflow-hidden z-20"
        >
          {/* Identity header — answers "who am I logged in as?". */}
          <div className="px-3 py-2.5 border-b border-black/5 dark:border-white/10">
            <div className="text-sm font-medium text-ink dark:text-ink-dark truncate">
              {profile.prenom} {profile.nom}
            </div>
            <div className="text-xs text-muted dark:text-muted-dark truncate">
              {profile.email}
            </div>
          </div>

          {/* Profile link */}
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark/50"
          >
            <User className="h-4 w-4 text-muted dark:text-muted-dark" />
            Mon profil
          </Link>

          {isAdmin && (
            <Link
              to="/admin/parametrage"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark/50"
            >
              <Settings2 className="h-4 w-4 text-muted dark:text-muted-dark" />
              Paramétrage
              {pendingAliasCount > 0 && (
                <span className="ml-auto rounded-full bg-chip-orange/10 px-2 py-0.5 text-[11px] font-medium leading-none text-chip-orange">
                  {pendingAliasCount} à valider
                </span>
              )}
            </Link>
          )}

          {/* Theme picker — segmented, all 3 states visible */}
          <div className="px-3 pt-2.5 pb-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted dark:text-muted-dark mb-1.5">
              Apparence
            </div>
            <ThemeSegmented />
          </div>

          {/* Logout, isolated by a divider so it's never a slip-click */}
          <div className="border-t border-black/5 dark:border-white/10">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                logout.mutate();
                window.location.href = "/login";
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark/50"
            >
              <LogOut className="h-4 w-4 text-muted dark:text-muted-dark" />
              Se déconnecter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 3-state segmented theme picker. Lives inside UserMenu — the
 * outside-the-menu cycling toggle was demoted because (a) cycling
 * burns a click to reach a known target state, and (b) theme is
 * a once-set preference, not something operators reach for daily.
 */
function ThemeSegmented() {
  const { theme, setTheme } = useTheme();
  const options: { value: Theme; label: string; Icon: typeof Sun }[] = [
    { value: "system", label: "Système", Icon: Monitor },
    { value: "light", label: "Clair", Icon: Sun },
    { value: "dark", label: "Sombre", Icon: Moon },
  ];
  return (
    <div className="grid grid-cols-3 gap-1 rounded-lg bg-canvas dark:bg-canvas-dark/60 p-0.5">
      {options.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            aria-pressed={active}
            title={label}
            className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-md text-xs transition ${
              active
                ? "bg-white dark:bg-surface-dark text-ink dark:text-ink-dark shadow-sm ring-1 ring-black/10 dark:ring-white/10"
                : "text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
